import { useId } from 'react';
import { PlusIcon, TrashIcon } from '@phosphor-icons/react';
import { IconTooltip } from '@/components/common/IconTooltip';
import { Num, Plural, T, useGT } from 'gt-react';
import {
  isWorkbookValueListOperator,
  workbookFilterSchema,
  type WorkbookColumnarSheet,
} from '@erp/shared-types';
import type { ShownDistinctValues } from './sampleDistinctValues';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { SheetPlan } from '@erp/workbooks';
import type { ChangeSheet } from './useWorkbookEditor';
import { WorksheetValueListFilter } from './WorksheetValueListFilter';
import { isoToLocalInput, localInputToIso } from './workbookDateTimeInput';
import type { NotePendingFilter } from './worksheetValueList';
import {
  CONDITION_OPERATORS,
  filterTakesValue,
  filterValuesSummary,
} from './worksheetFilterOperators';
import {
  useSortDirectionLabel,
  worksheetFieldType,
} from './worksheetTypeLabels';

type Filter = WorkbookColumnarSheet['filters'][number];
const SORT_DIRECTIONS = ['asc', 'desc'] as const;
/** The Sort picker's value for an unsorted column. */
const NO_SORT = 'none';

export function useFilterOperatorLabels(): Record<Filter['operator'], string> {
  const gt = useGT();
  return {
    eq: gt('Equals', { context: 'worksheet filter operator label' }),
    neq: gt('Does not equal', { context: 'worksheet filter operator label' }),
    gt: gt('Greater than', { context: 'worksheet filter operator label' }),
    gte: gt('Greater than or equal to', {
      context: 'worksheet filter operator label',
    }),
    lt: gt('Less than', { context: 'worksheet filter operator label' }),
    lte: gt('Less than or equal to', {
      context: 'worksheet filter operator label',
    }),
    contains: gt('Contains', { context: 'worksheet filter operator label' }),
    'is-null': gt('Is empty', { context: 'worksheet filter operator label' }),
    'is-not-null': gt('Is not empty', {
      context: 'worksheet filter operator label',
    }),
    'is-error': gt('Is error', { context: 'worksheet filter operator label' }),
    'is-not-error': gt('Is not error', {
      context: 'worksheet filter operator label',
    }),
    'is-na': gt('Is #N/A', { context: 'worksheet filter operator label' }),
    'is-one-of': gt('Is one of', {
      context: 'worksheet filter operator label',
    }),
    'is-not-one-of': gt('Is not one of', {
      context: 'worksheet filter operator label',
    }),
  };
}

/** A Value list filter's values for people: "West, (Empty), North and 2 more". */
export function useFilterValuesText(): (filter: Filter) => string {
  const gt = useGT();
  const empty = gt('(Empty)', { context: 'worksheet value list empty entry' });
  return (filter) => {
    const { shown, more } = filterValuesSummary(filter, empty);
    return more
      ? gt('{values} and {count} more', {
          context: 'worksheet value list filter values, abbreviated',
          values: shown,
          count: String(more),
        })
      : shown;
  };
}

/** Names a filter for people, e.g. "Cases Greater Than 0". */
export function useFilterDescription(): (filter: Filter) => string {
  const gt = useGT();
  const labels = useFilterOperatorLabels();
  const valuesText = useFilterValuesText();
  return (filter) =>
    isWorkbookValueListOperator(filter.operator)
      ? gt('{column} {operator} {value}', {
          context: 'worksheet filter description',
          column: filter.column,
          operator: labels[filter.operator],
          value: valuesText(filter),
        })
      : !filterTakesValue(filter.operator)
        ? gt('{column} {operator}', {
            context: 'worksheet filter description without value',
            column: filter.column,
            operator: labels[filter.operator],
          })
        : gt('{column} {operator} {value}', {
            context: 'worksheet filter description',
            column: filter.column,
            operator: labels[filter.operator],
            value: filter.value,
          });
}

/** The rows one filter removed in the Result's Run. */
function FilterRemovedCount({
  count,
  stale,
}: {
  count: number;
  stale: boolean;
}) {
  return (
    <p className="col-span-2 text-xs text-muted-foreground tabular-nums">
      {stale ? (
        <T context="worksheet filter removed rows in a stale run">
          Removed <Num>{count}</Num> <Plural n={count} one="row" other="rows" />{' '}
          in the last run
        </T>
      ) : (
        <T context="worksheet filter removed rows">
          Removes <Num>{count}</Num> <Plural n={count} one="row" other="rows" />
        </T>
      )}
    </p>
  );
}

export function WorksheetFilters({
  sheet,
  sheetPlan,
  column,
  change,
  distinct,
  removals = [],
  stale = false,
  notePending,
}: {
  sheet: WorkbookColumnarSheet;
  /** Gives input and Lookup Fields their Declared types for the sort labels. */
  sheetPlan?: SheetPlan;
  column: string;
  change: ChangeSheet;
  /** The column's Distinct values in the shown Result, for its value list. */
  distinct?: ShownDistinctValues;
  /** Rows each of the sheet's filters removed in the Result's Run, by filter index. */
  removals?: readonly (number | undefined)[];
  /** Whether that Result is Stale. */
  stale?: boolean;
  /** Told of value list changes that filter rows out, so the grid can hint at them. */
  notePending?: NotePendingFilter;
}) {
  const gt = useGT();
  const sortLabelId = useId();
  const labels = useFilterOperatorLabels();
  const valuesText = useFilterValuesText();
  const sortLabel = useSortDirectionLabel();
  const type = worksheetFieldType(sheet, column, sheetPlan);
  const currentSort = sheet.sort.find((item) => item.column === column);
  function updateFilter(index: number, patch: Partial<Filter>) {
    change((latest) => ({
      ...latest,
      filters: latest.filters.map((item, i) =>
        i === index ? { ...item, ...patch } : item,
      ),
    }));
  }
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-xs">
        <span id={sortLabelId}>
          <T context="worksheet column sorting">Sort</T>
        </span>
        <Select
          value={currentSort?.direction ?? NO_SORT}
          onValueChange={(direction) => {
            change((latest) => ({
              ...latest,
              sort:
                direction === 'asc' || direction === 'desc'
                  ? latest.sort.some((item) => item.column === column)
                    ? latest.sort.map((item) =>
                        item.column === column ? { ...item, direction } : item,
                      )
                    : [...latest.sort, { column, direction }]
                  : latest.sort.filter((item) => item.column !== column),
            }));
          }}
        >
          <SelectTrigger
            size="sm"
            className="min-w-36"
            aria-labelledby={sortLabelId}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NO_SORT}>
              {gt('None', { context: 'worksheet no column sort' })}
            </SelectItem>
            {SORT_DIRECTIONS.map((direction) => (
              <SelectItem
                key={direction}
                value={direction}
                disabled={!currentSort && sheet.sort.length >= 10}
              >
                {sortLabel(type, direction)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <section
        className="space-y-2"
        aria-label={gt('Filter by values', {
          context: 'worksheet value list section',
        })}
      >
        <h5 className="text-xs font-medium">
          <T context="worksheet value list section">Filter by values</T>
        </h5>
        <WorksheetValueListFilter
          sheet={sheet}
          column={column}
          type={type}
          distinct={distinct}
          change={change}
          notePending={notePending}
        />
      </section>
      <section
        className="space-y-2"
        aria-label={gt('Filter by condition', {
          context: 'worksheet condition filters section',
        })}
      >
        <div className="flex items-center gap-2">
          <h5 className="flex-1 text-xs font-medium">
            <T context="worksheet condition filters section">
              Filter by condition
            </T>
          </h5>
          <Button
            size="sm"
            variant="outline"
            disabled={sheet.filters.length >= 30}
            onClick={() =>
              change((latest) => ({
                ...latest,
                filters: [
                  ...latest.filters,
                  { column, operator: 'eq', value: '' },
                ],
              }))
            }
          >
            <PlusIcon />
            <T context="worksheet add selected column filter">Add filter</T>
          </Button>
        </div>
        {sheet.filters.some((filter) => filter.column === column) && (
          <p className="text-xs text-muted-foreground">
            <T context="worksheet filter combination">
              After formulas, keep rows matching all filters. Only the error
              filters match a cell showing an error such as #N/A.
            </T>
          </p>
        )}
        {sheet.filters.map((filter, index) =>
          filter.column !== column ? null : (
            <div
              key={index}
              className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-2 gap-y-1"
            >
              {isWorkbookValueListOperator(filter.operator) ? (
                <p className="min-w-0 truncate text-xs">
                  {labels[filter.operator]}{' '}
                  <span className="text-muted-foreground">
                    {valuesText(filter)}
                  </span>
                </p>
              ) : (
                <>
                  <Select
                    value={filter.operator}
                    onValueChange={(operator) =>
                      updateFilter(index, {
                        operator:
                          workbookFilterSchema.shape.operator.parse(operator),
                      })
                    }
                  >
                    <SelectTrigger
                      className="w-full min-w-0"
                      aria-label={gt('Condition', {
                        context: 'worksheet filter operator',
                      })}
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CONDITION_OPERATORS.map((operator) => (
                        <SelectItem key={operator} value={operator}>
                          {labels[operator]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {filterTakesValue(filter.operator) && (
                    // Below its condition, as a spreadsheet's filter menu
                    // lays them out: the menu is too narrow for both on a row.
                    <Input
                      className="col-start-1 row-start-2 w-full min-w-0"
                      // A Date and time is typed in local time and kept as UTC.
                      type={type === 'datetime' ? 'datetime-local' : 'text'}
                      value={
                        type === 'datetime'
                          ? isoToLocalInput(filter.value)
                          : filter.value
                      }
                      aria-label={gt('Filter value', {
                        context: 'worksheet filter value',
                      })}
                      onChange={(event) =>
                        updateFilter(index, {
                          value:
                            type === 'datetime'
                              ? localInputToIso(event.target.value)
                              : event.target.value,
                        })
                      }
                    />
                  )}
                </>
              )}
              <IconTooltip>
                <Button
                  size="sm"
                  variant="ghost"
                  className="col-start-2 row-start-1"
                  aria-label={gt('Remove filter', {
                    context: 'worksheet remove filter',
                  })}
                  onClick={() =>
                    change((latest) => ({
                      ...latest,
                      filters: latest.filters.filter((_, i) => i !== index),
                    }))
                  }
                >
                  <TrashIcon />
                </Button>
              </IconTooltip>
              {removals[index] !== undefined && (
                <FilterRemovedCount count={removals[index]} stale={stale} />
              )}
            </div>
          ),
        )}
      </section>
    </div>
  );
}
